/* ⛔⛤ ~~useCallback, useEffect~~ — both belonged to the struck scan-folder
   apparatus (the permission-recall effect and `refreshScans`). Nothing else in
   this file ever used either. */
import { useRef, useState } from 'react';
import { isFileDrop, snapshotFiles } from '@/lib/dnd';
import { useFileUpload } from '@/lib/useFileUpload';
/* ⛔⛤ ~~@/lib/scanFolder~~ — no longer imported here. The library itself stays:
   `ScanFolderPanel` is its real owner and is untouched. */

/**
 * ONE "DROP FILES HERE, OR BROWSE" CONTROL, FOR EVERY SCREEN THAT WANTS ONE (#303).
 *
 * ---- ⭐ WHY IT EXISTS -------------------------------------------------------
 *
 * Pete, 2026-08-27, on the Paperwork section of Edit A00000246: *"Need to be able
 * to add paperwork here too."*
 *
 * ⛔ AND THE ANSWER WAS DELIBERATELY NOT "COPY THE BLOCK OUT OF PaperworkSliver".
 * Four screens want this control, and four private copies of a drop handler is
 * how three of them end up with three DIFFERENT bugs. ⚠️ Measured 2026-09-08: it
 * had reached exactly ONE of the four. **The loop now lives in
 * `lib/useFileUpload.ts` and all three screens share THAT**, each keeping its own
 * presentation — see that file's header for the five rules it holds.
 *
 * ---- ⭐⭐ THE SCAN FOLDER LIVES HERE TOO, AND THAT IS THE POINT -------------
 *
 * > Pete, 2026-09-08: *"wherever we have the paperwork feature, this needs to be
 * > updated I believe."*
 *
 * ⛔ HE IS RIGHT, AND THE ANSWER IS TO ADD IT ONCE. A browser can never be
 * pointed at `C:\Scans` — no API, no flag, and there never will be, because a
 * page that could read arbitrary paths could read anything on the machine. What
 * it CAN do is be handed the folder once, through the browser's own picker, and
 * remember it. ⭐ `scanFolder.ts` keeps that handle in IndexedDB, so ONE pick
 * serves every screen: new appointment, edit, any driver card, tomorrow.
 * ⚠️ It outlives logout — IndexedDB is per-origin and signing out does not touch
 * it.
 *
 * ⛔⛔ **"PERMISSION LAPSED" MUST NEVER RENDER AS AN EMPTY FOLDER.** Chrome 122+
 * offers *"Allow on every visit"*; without it, permission drops back to `prompt`
 * once every tab closes, and it can only be re-granted FROM A CLICK. ⚠️ Asking
 * outside a user gesture throws — so a control that queried non-interactively
 * and rendered the result would show a folder with nothing in it, which reads as
 * "no scans yet" rather than "you have lost access". That is the whole reason
 * `needsReconnect` is a distinct state below, and it is the same failure shape
 * that has cost this project a day at a time: **a plausible-looking output
 * instead of an error.**
 *
 * ⚠️ IT DOES NOT POLL, and `ScanFolderPanel` does. The panel is a tray somebody
 * watches; this is a control somebody opens. Re-reading a folder every few
 * seconds from every mounted drop zone on the page would be work nobody asked
 * for — so it reads on open, and offers a refresh.
 *
 * ⚠️ IT DOES NOT GREY OUT ALREADY-FILED SCANS. That needs the appointment context
 * and a `knownScans` lookup, and it is `ScanFolderPanel`'s job. ⛔ The two are
 * different acts and both stay: the panel is where you BROWSE the folder and file
 * onto a load; this is where you ATTACH to the thing already in front of you.
 *
 * ---- ⛔ WHAT THIS COMPONENT STILL OWNS -------------------------------------
 *
 * ⛔ **IT REFUSES THE APP'S OWN DRAG.** A scan card dragged out of the tray
 * carries the paperwork MIME type; treating it as a file would re-upload a
 * document we already hold, under a second id.
 *
 * ⛔ **BOTH `preventDefault` AND `stopPropagation` ON DRAG-OVER.** Without
 * preventDefault the browser keeps its "no drop here" cursor and then NAVIGATES
 * to the file on release — the tab replaced by a PDF, which reads as a crash.
 *
 * ⚠️ IT IS NOT `pointer-events-none` WHILE BUSY. That was the OTHER candidate
 * cause of Pete's silent drop: `PaperworkSliver` wrapped its whole body,
 * including the drop zone, in `pointer-events-none` while any action was in
 * flight — so a drop attempted at the wrong moment was swallowed by the browser
 * before React ever saw it. Here the zone stays live and the second drop simply
 * queues behind the first.
 *
 * ⚠️ IT DOES NOT KNOW WHERE THE FILE GOES. The caller supplies `upload`, because
 * "into the facility tray, unfiled" and "onto THIS appointment" are different
 * acts and only the caller knows which one it is.
 */

/**
 * ⚠️ A FALLBACK, NOT AN AUTHORITY. `dock.DocumentConfig` decides what a site
 * accepts, and a caller holding that config should pass `scanExtensions`. ⛔ This
 * list exists so a screen without the config still filters SOMETHING — a folder
 * listing every `.tmp` the scanner leaves behind is worse than a short list.
 */
/* ⛔⛤ ~~DEFAULT_SCAN_EXTENSIONS~~ — struck with the folder listing it filtered. */

/** `.pdf,.jpg,image/*` -> `['pdf','jpg']`. MIME entries carry no extension. */
/* ⛔⛤ ~~extensionsFromAccept()~~ — struck; only the folder listing filtered by it. */

export function FileDropZone({
    getToken,
    upload,
    onDone,
    label = 'Drop files here, or browse',
    accept,
    disabled = false,
    className = '',
    /* ⛔⛤ ~~scanFolder = true~~ / ~~scanExtensions~~ — struck 2026-09-22.
       ⚠️ A REMOVED PROP IS A SIGNATURE CHANGE, so both call sites were read, not
       recalled: AppointmentDocumentsDialog passed neither (so this control was
       already inert there) and PaperworkList passed only `scanFolder`. Nobody
       ever passed `scanExtensions`. */
}: {
    getToken: () => Promise<string>;
    /** ⚠️ Called once PER FILE, sequentially. See useFileUpload rule 2. */
    upload: (token: string, file: File) => Promise<unknown>;
    /** Reload whatever list this zone feeds. Called once, after ALL files. */
    onDone?: () => void | Promise<void>;
    label?: string;
    accept?: string;
    disabled?: boolean;
    className?: string;
    /* ⛔⛤ ~~scanFolder?: boolean~~ / ~~scanExtensions?: string[]~~ — struck.
       ⚠️ The reasoning for `scanFolder` defaulting to TRUE is worth keeping even
       though the prop is gone: it was what made "wherever we have the paperwork
       feature" true without editing four screens. That instinct is right and
       belongs to whatever replaces it; it is not an argument for keeping a second
       copy of a control the header already carries. */
    /* ⚠️ ~~`autoBrowse`~~ LIVED HERE FOR ABOUT AN HOUR on 2026-09-08 and is
       recorded rather than forgotten. It opened the picker on mount, so a dialog
       could show a drop zone AND the OS picker at once. ⛔ It relied on transient
       activation surviving a React mount — a WINDOW, not a guarantee — and the
       screen it was built for stopped using a dialog at all: the paperclip now
       calls `.click()` synchronously inside its own handler, which cannot fail
       that way in any browser. ⭐ If a future caller wants this, do it that way
       instead; there is no reason to reintroduce the timing dependency. */
}) {
    const [over, setOver] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);


    /**
     * ⭐ THE LOOP LIVES IN `lib/useFileUpload.ts`. Snapshot, sequential, count on
     * success, ⛔ count on failure too.
     */
    const { busy, added, error, run } = useFileUpload({ getToken, upload, onDone });

    // ---- ⛔⛤ the scan folder, struck 2026-09-22 — see the note above the import

    /* ⛔⛤ ~~the permission-recall effect~~, ~~refreshScans~~, ~~connectFolder~~,
       ~~openList~~ and ~~addTicked~~ — struck 2026-09-22 with the control they fed.
       ⚠️ ONE OF THEM IS WORTH REMEMBERING RATHER THAN JUST DELETING: the recall
       effect was deliberately NON-INTERACTIVE, because `requestPermission` throws
       outside a user gesture and a throw on mount leaves a control looking broken.
       `ScanFolderPanel` makes the same distinction; if it ever stops, this is the
       note that says why it mattered. */

    /**
     * ⛔ `disabled` IS GUARDED HERE, NOT IN THE HOOK. The hook uploads what it is
     * given; whether this particular control is currently accepting anything is a
     * question about the control.
     */
    function start(files: File[]) {
        if (disabled) return;
        void run(files);
    }

    return (
        <div className={className}>
            <div
                onDragOver={(e) => {
                    if (!isFileDrop(e.dataTransfer)) return;
                    /* ⛔ BOTH, AND BOTH ARE REQUIRED. See the header. */
                    e.preventDefault();
                    e.stopPropagation();
                    setOver(true);
                }}
                onDragLeave={() => setOver(false)}
                onDrop={(e) => {
                    if (!isFileDrop(e.dataTransfer)) return;
                    e.preventDefault();
                    e.stopPropagation();
                    setOver(false);
                    /* ⛔⛔ SNAPSHOT HERE, INSIDE THE EVENT. The hook snapshots too —
                       this is belt and braces, and the belt has already failed once
                       (#288). */
                    start(snapshotFiles(e.dataTransfer.files));
                }}
                onClick={() => { if (!busy && !disabled) inputRef.current?.click(); }}
                role="button"
                tabIndex={disabled ? -1 : 0}
                aria-disabled={disabled}
                onKeyDown={(e) => {
                    if (e.key !== 'Enter' && e.key !== ' ') return;
                    e.preventDefault();
                    if (!busy && !disabled) inputRef.current?.click();
                }}
                className={'rounded border border-dashed px-2 py-3 text-center text-[11px] '
                    + (disabled
                        ? 'cursor-not-allowed opacity-50 '
                        : 'cursor-pointer ')
                    + (over
                        ? 'border-sky-400 bg-sky-50 text-sky-900'
                        : 'text-muted-foreground hover:bg-muted/50')}
            >
                {busy ? 'Uploading…' : label}
            </div>

            {/**
              * ⚠️ SNAPSHOT, THEN CLEAR, THEN RUN — IN THAT ORDER. `run` is async:
              * clearing the input on the line after it would clear the live
              * FileList while the first await was still pending, which is the
              * browse half of #288. ⭐ Clearing here rather than in a `finally` is
              * deliberate — it lets the same file be picked twice in a row, which
              * is a thing people do when they think the first go did not work.
              */}
            <input
                ref={inputRef}
                type="file"
                multiple
                accept={accept}
                className="hidden"
                onChange={(e) => {
                    const files = snapshotFiles(e.target.files);
                    e.target.value = '';
                    start(files);
                }}
            />

            {/* ⛔⛤ the scan-folder block — struck 2026-09-22, see the header note. */}

            {error && (
                <div className="mt-1 rounded border border-destructive/40 bg-destructive/10 px-2 py-1 text-[11px] text-destructive">
                    {error}
                </div>
            )}
            {added !== null && !error && (
                <div className="mt-1 text-[11px] text-emerald-700">
                    {added} file{added === 1 ? '' : 's'} added.
                </div>
            )}
            {added !== null && error && (
                <div className="mt-1 text-[11px] text-muted-foreground">
                    {added} file{added === 1 ? '' : 's'} did upload before that.
                </div>
            )}
        </div>
    );
}
